'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import * as React from 'react'
import { Formik, FormikHelpers, useFormikContext } from 'formik'
import ReactDOM from 'react-dom'
import moment from 'moment'

// walrus
import {
  accessibilityLabel,
  ThemeVariantProvider
} from '@components/kit/_support'
import submitForm from '@lib/submit-form'
import useLoadingState from '../use-loading-state'
import {
  useSelect,
  SelectButton,
  SelectMenu,
  SelectOption
} from '@components/kit/select'
import Box from '@components/kit/box'
import Card from '@components/kit/card'
import Form, {
  SubmitButton,
  TextField,
  DateField,
  TimeField
} from '@components/kit/form'
import { Column, Row } from '@components/kit/grid'
import Text from '@components/kit/text'
import { titleCase } from '@utils/string-formatters'
import { TextInput } from '@components/kit/form/inputs'
import Pressable from '@components/kit/pressable'
import DateTimePicker, {
  ImperativeDateTimePickerProps
} from '@components/kit/datetime-picker'

// lib
import { ChatContext } from '../chat.web'
import { useCallsStore } from '@store/Calls'
import { IRecordCall } from '@store/Calls/types'

/* -----------------------------------------------------------------------------
 * types
 * -------------------------------------------------------------------------- */

// ids of the call results the backend stores; the names are what the select shows
type CallResultIds =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15
  | 16
  | 17
  | 18
  | 19
  | 20
  | 21
  | 22
  | 23
  | 24
  | 25
  | 26
  | 27
  | 28
  | 29

const callResultOptions: { id: CallResultIds; name: string }[] = [
  { id: 1, name: 'Member - No Answer' },
  { id: 2, name: 'Member - Left Voicemail' },
  { id: 3, name: 'Member - Requested Callback' },
  { id: 4, name: 'Member - Requested Removal' },
  { id: 5, name: 'Member - Had Discussion' },
  { id: 27, name: 'Member - Sent SMS' },
  { id: 6, name: 'Provider - No Answer' },
  { id: 7, name: 'Provider - Left Voicemail' },
  { id: 8, name: 'Provider - Requested Callback' },
  { id: 9, name: 'Provider - Requested Removal' },
  { id: 10, name: 'Provider - Had Discussion' },
  { id: 28, name: 'Provider - Sent Fax' },
  { id: 11, name: 'Pharmacy - No Answer' },
  { id: 12, name: 'Pharmacy - Left Voicemail' },
  { id: 13, name: 'Pharmacy - Requested Callback' },
  { id: 14, name: 'Pharmacy - Requested Removal' },
  { id: 15, name: 'Pharmacy - Had Discussion' },
  { id: 16, name: 'Plan - No Answer' },
  { id: 17, name: 'Plan - Left Voicemail' },
  { id: 18, name: 'Plan - Requested Callback' },
  { id: 19, name: 'Plan - Requested Removal' },
  { id: 20, name: 'Plan - Had Discussion' },
  { id: 21, name: 'Other - No Answer' },
  { id: 22, name: 'Other - Left Voicemail' },
  { id: 23, name: 'Other - Requested Callback' },
  { id: 24, name: 'Other - Requested Removal' },
  { id: 25, name: 'Other - Had Discussion' },
  { id: 26, name: 'Pharmacist - Pre Work' },
  { id: 29, name: 'Client - Care Collaboration' }
]

export type FormValues = {
  date: string
  duration: string
  result: CallResultIds | null
  time: string
}

/* -----------------------------------------------------------------------------
 * utils
 * -------------------------------------------------------------------------- */

const msToHrs = (ms: number) => Math.floor(ms / (60 * 60 * 1000))

const msToMins = (ms: number) =>
  Math.floor((ms - msToHrs(ms) * 60 * 60 * 1000) / (60 * 1000))

/* -----------------------------------------------------------------------------
 * DateFieldPicker
 * -------------------------------------------------------------------------- */

const DateFieldPicker = React.forwardRef<ImperativeDateTimePickerProps, {}>(
  (props, ref) => {
    const { setFieldValue } = useFormikContext<FormValues>()

    return (
      <DateTimePicker
        onSubmit={date => setFieldValue('date', date.format('YYYY-MM-DD'))}
        ref={ref}
        dismissOnOverlayClick={false}
        maxDate={moment()}
        submitOnDateChange={true}
        position='fixed'
        bottom={45}
      />
    )
  }
)

/* -----------------------------------------------------------------------------
 * Calls
 * -------------------------------------------------------------------------- */

const Calls = () => {
  const { userId } = React.useContext(ChatContext)
  const { getUserCalls, logUserCall } = useCallsStore.getState()
  const [userCalls, setUserCalls] = React.useState<IRecordCall[]>([])
  const buttonRef = React.useRef(null)
  const menuRef = React.useRef(null)
  const searchInputRef = React.useRef(null)
  const dateTimePickerRef = React.useRef<ImperativeDateTimePickerProps>(null)
  const [searchedCallResult, setSearchedCallResult] = React.useState('')

  const filteredCallResultItems = callResultOptions.filter(
    val => titleCase(val.name).indexOf(titleCase(searchedCallResult)) >= 0
  )

  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    reset: resetSelect
  } = useSelect({
    buttonRef,
    menuRef,
    items: filteredCallResultItems
  })

  React.useEffect(() => {
    setSearchedCallResult('')
    searchInputRef.current.focus()
  }, [isOpen])

  const openDateTimePicker = () => {
    dateTimePickerRef.current.present()
  }

  const handleSubmit = submitForm(
    async (
      { date, time, duration }: FormValues,
      { resetForm, setFieldError }: FormikHelpers<FormValues>
    ) => {
      const isFutureDate = moment(date).isAfter(moment.now())

      if (isFutureDate) {
        return setFieldError(
          'date',
          'Please, review the provided date. It must be a past date'
        )
      }

      const datetime = moment(`${date}T${time}`)

      if (datetime.isValid()) {
        await logUserCall(userId, {
          date: datetime.toISOString(),
          duration: parseInt(duration) * 60 * 1000,
          result: selectedItem.id
        })
        // the create endpoint only acknowledges, so reload the list
        setUserCalls(await getUserCalls(userId))
        resetSelect()
        resetForm()
      } else {
        const isDateValid = moment(date).isValid()

        if (isDateValid) {
          setFieldError('time', 'Invalid time provided')
        } else {
          setFieldError('date', 'Invalid date provided')
        }
      }
    }
  )

  const { isLoaded } = useLoadingState(async () => {
    setUserCalls(await getUserCalls(userId))
  }, [userId])

  return (
    <Box pt='24' {...accessibilityLabel('Calls')}>
      <Text ml='12' mb='12' font='display.16.bold'>
        Calls
      </Text>
      <Card px={isLoaded ? '00' : '16'} py={isLoaded ? '00' : '24'}>
        {isLoaded && (
          <React.Fragment>
            <ThemeVariantProvider value='punchThroughGray'>
              <Formik
                initialValues={{
                  date: '',
                  time: '',
                  result: null,
                  duration: ''
                }}
                onSubmit={handleSubmit}
              >
                <Form>
                  <Row flexWrap='wrap' width='100%'>
                    <DateFieldPicker ref={dateTimePickerRef} />
                    <Column width={148}>
                      <Pressable
                        onPress={openDateTimePicker}
                        width='100%'
                        height='100%'
                      >
                        <DateField
                          name='date'
                          m={-1 as any}
                          borderColor='shade03'
                          borderRadius='none'
                          showErrorMessage={true}
                        />
                      </Pressable>
                    </Column>
                    <Column width={108}>
                      <TimeField
                        name='time'
                        m={-1 as any}
                        borderColor='shade03'
                        borderRadius='none'
                        showErrorMessage={false}
                      />
                    </Column>
                    <Column width={148}>
                      <Box
                        ref={buttonRef}
                        m={-1 as any}
                        borderWidth={1}
                        borderColor='shade03'
                        bg='white'
                      >
                        <SelectButton
                          {...getToggleButtonProps()}
                          px='16'
                          height={45}
                          borderRadius='none'
                        >
                          <Text fontSize={15} fontWeight='500'>
                            {selectedItem ? selectedItem.name : 'Result'}
                          </Text>
                        </SelectButton>
                      </Box>
                    </Column>
                    <Column width={108}>
                      <TextField
                        name='duration'
                        label='Duration'
                        placeholder='min'
                        required
                        m={-1 as any}
                        borderColor='shade03'
                        borderRadius='none'
                        showErrorMessage={false}
                      />
                    </Column>
                    <Column flexDirection='row' width='100%'>
                      <SubmitButton
                        flex='1 0 auto'
                        m={-1 as any}
                        borderWidth={1}
                        borderColor='shade03'
                        borderRadius='none'
                        bg='white'
                        variant='punchThroughViolet'
                      >
                        SAVE
                      </SubmitButton>
                    </Column>
                  </Row>
                </Form>
              </Formik>
            </ThemeVariantProvider>
            {userCalls && userCalls.length ? (
              <Box zIndex={-1} px='16' py='24' bg='shade01'>
                {userCalls.map((call, index) => (
                  <Box
                    key={call._id}
                    flexDirection='row'
                    flexWrap='wrap'
                    justifyContent='space-between'
                    mb={index === userCalls.length - 1 ? '00' : '16'}
                  >
                    <Text flex='1 1 auto' variant='subtitle' width={240}>
                      {
                        callResultOptions.find(item => item.id === call.result)
                          .name
                      }
                      {/*
                        As we have multiple "HAD_DISCUSSION" options,
                        we are now checking by these log ids
                      */}
                      {[5, 10, 15, 20, 25].includes(call.result)
                        ? ` (${msToHrs(call.duration)}hr ${msToMins(
                            call.duration
                          )}min)`
                        : ''}
                    </Text>
                    <Text flex='0 0 auto' variant='meta'>
                      {moment(call.date).format('hh:mm A')} &bull;{' '}
                      {moment(call.date).format('MM/DD/YYYY')}
                    </Text>
                  </Box>
                ))}
              </Box>
            ) : null}
          </React.Fragment>
        )}
      </Card>
      {ReactDOM.createPortal(
        <ThemeVariantProvider value='gray'>
          <SelectMenu
            px='08'
            minWidth='250px'
            maxHeight='340px'
            {...getMenuProps()}
          >
            <TextInput
              ref={searchInputRef}
              value={searchedCallResult}
              pb='04'
              borderColor='shade03'
              borderBottomWidth={2}
              textColor='shade06'
              placeholder='Search call result...'
              placeholderTextColor={'shade05'}
              onChangeText={text => setSearchedCallResult(text)}
            />
            <Box overflowY='scroll' maxHeight='295px'>
              {filteredCallResultItems.map((item, index) => (
                <SelectOption.Title
                  fontSize={13}
                  fontWeight='500'
                  mt='08'
                  py='04'
                  {...getItemProps({ item, index })}
                  key={item.id}
                >
                  {item.name}
                </SelectOption.Title>
              ))}
            </Box>
          </SelectMenu>
        </ThemeVariantProvider>,
        document.getElementById('root')
      )}
    </Box>
  )
}

export default Calls
